import { ref, reactive, computed, watch } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { trackEvent, trackStarShopBounceEvent } from '@/Helpers/analytics'
import { openStarPackPurchaseUrl } from '@/Helpers/starPackPurchase'

/**
 * Shared Star Shop purchase / membership-upsell / buy-stars-modal / analytics
 * flow, extracted from StarShop/Index.vue so StarShop/Vault.vue can reuse the
 * exact same tested purchase codepath instead of a second copy of it.
 *
 * Every param is a ref/computed owned by the caller — this composable doesn't
 * own product data, just the purchase/modal machinery layered on top of it.
 *
 * @param {Object} deps
 * @param {import('vue').Ref|import('vue').ComputedRef} deps.analyticsContext
 * @param {import('vue').ComputedRef<Record<number,string>>} deps.mechanicByProductId
 * @param {import('vue').ComputedRef<Record<number,string>>} deps.productNameById
 * @param {import('vue').ComputedRef<Record<number,string|null>>} deps.performerNameById
 * @param {import('vue').ComputedRef<Record<number,string|null>>} deps.performerPhotoById
 * @param {import('vue').ComputedRef<number>} deps.offersCount
 * @param {import('vue').Ref|import('vue').ComputedRef} deps.shopUrl
 * @param {string} [deps.location] — analytics `location` field, e.g. 'shop_page' / 'shop_vault_page'
 */
export function useStarShopPurchase({
    analyticsContext,
    mechanicByProductId,
    productNameById,
    performerNameById,
    performerPhotoById,
    offersCount,
    shopUrl,
    location = 'shop_page',
}) {
    const page = usePage()
    const balance = computed(() => Number(page.props.auth?.user?.wallet_balance) || 0)
    const isUserLoggedIn = computed(() => !!page.props.auth?.user?.id)

    function track(event, extra = {}, productId = null) {
        trackEvent(event, {
            source: 'star_shop_page',
            location,
            location_id: null,
            star_balance: balance.value,
            offers_count: offersCount.value,
            cart_enabled: null,
            membership_lifecycle_stage: analyticsContext.value?.membership_lifecycle_stage ?? null,
            membership_term: analyticsContext.value?.membership_term ?? null,
            membership_tier: analyticsContext.value?.membership_tier ?? null,
            is_trial: analyticsContext.value?.is_trial ?? null,
            user_id: page.props.auth?.user?.id ?? null,
            product_id: productId,
            product_type: productId ? (mechanicByProductId.value[productId] ?? null) : null,
            product_name: productId ? (productNameById.value[productId] ?? null) : null,
            ...extra,
        })
    }

    function joinRedirect(source) {
        track('Star Shop Guest Join Redirected', { redirect_source: source })
        window.location.href = route('www.join.signUp', { custom6: source })
    }

    const copied = ref(null)

    function copyShareLink(productId) {
        const url = shopUrl.value + '?product=' + productId
        const done = () => {
            copied.value = productId
            setTimeout(() => { if (copied.value === productId) copied.value = null }, 2000)
        }
        const fallback = () => {
            const el = document.createElement('textarea')
            el.value = url
            el.style.position = 'fixed'
            el.style.opacity = '0'
            document.body.appendChild(el)
            el.focus()
            el.select()
            document.execCommand('copy')
            document.body.removeChild(el)
            done()
        }
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(url).then(done).catch(fallback)
        } else {
            fallback()
        }
        track('Star Shop Share Link Copied', {}, productId)
    }

    // ─── Purchase / contribute ─────────────────────────────────────────────
    const purchasing = ref(false)
    const purchaseFlash = reactive({ message: '', type: '' })
    const membershipUpsell = reactive({ open: false, status: 'expired', intent: '', performerName: '', performerPhoto: '', productId: null, productTitle: '', productKind: '', productStars: null, performerId: null })
    const successModal = reactive({ open: false, message: '', discountItemValue: null, mechanic: null, stats: null, interstitialAd: null, interstitialAdSkipReason: null, productId: null, starCost: null, performerId: null })

    // Derived from the same `location` param that already distinguishes
    // Index.vue ('shop_page') from Vault.vue ('shop_vault_page') — matches
    // the literal `source` prop each page passes to <InterstitialAdModal>
    // (star_shop_page / star_shop_vault_page), so the Skipped event below
    // uses the same source value the Served/Clicked/Dismissed events would
    // have used had the ad not been skipped.
    const interstitialAdSource = location === 'shop_vault_page' ? 'star_shop_vault_page' : 'star_shop_page'
    let flashTimer = null

    /**
     * Product context for the wall funnel: Dismissed and CTA Clicked carry the
     * rejected product too, not just Shown, so the funnel can be segmented by
     * what was rejected without stitching events together on user + timestamp.
     * product_stars and performer_id aren't part of track()'s base, so they
     * ride along here (matching star_shop_trial_teaser.blade.php's payload).
     */
    function membershipUpsellTrackProps() {
        return {
            membership_status: membershipUpsell.status,
            intent: membershipUpsell.intent || null,
            source: 'star_shop_page',
            product_stars: membershipUpsell.productStars ?? null,
            performer_id: membershipUpsell.performerId ?? null,
        }
    }

    /**
     * @param {number|null} stars — the actual amount attempted, passed
     * explicitly by the caller (see contribute() below) rather than looked
     * up — same star_shop_widget.blade.php purchaseItem()/purchaseCart()
     * attribution convention. Only present when productId is, so a single
     * specific product is attributable if the member upgrades from here
     * (see MembershipUpsellModal.vue's onCtaClick).
     */
    function openMembershipUpsell(status, productId = null, stars = null, performerId = null) {
        const mechanic = productId ? (mechanicByProductId.value[productId] ?? '') : ''
        membershipUpsell.intent = mechanic === 'oflink' ? 'discount' : mechanic
        membershipUpsell.performerName = (productId ? performerNameById.value[productId] : '') ?? ''
        membershipUpsell.performerPhoto = (productId ? performerPhotoById.value[productId] : '') ?? ''
        membershipUpsell.productId = productId
        membershipUpsell.productTitle = (productId ? productNameById.value[productId] : '') ?? ''
        membershipUpsell.productKind = mechanic
        membershipUpsell.productStars = stars
        membershipUpsell.performerId = performerId
        membershipUpsell.status = status || 'expired'
        membershipUpsell.open = true
        track('Star Shop Membership Wall Shown', membershipUpsellTrackProps(), productId)
    }

    function closeMembershipUpsell() {
        membershipUpsell.open = false
        track('Star Shop Membership Wall Dismissed', membershipUpsellTrackProps(), membershipUpsell.productId)
    }

    function trackMembershipUpsellCta() {
        track('Star Shop Membership Wall CTA Clicked', membershipUpsellTrackProps(), membershipUpsell.productId)
    }

    // ─── Gamified stat badges + count-up ───────────────────────────────────
    const BADGE_ICONS = {
        first_purchase: 'sparkle',
        top_purchaser: 'trophy',
        top_fan: 'flame',
        top_percentile: 'trending',
        streak: 'zap',
        stars_milestone: 'brand-star',
        order_milestone: 'trophy',
        stars_spent: 'brand-star',
    }
    const COUNT_UP_TYPES = ['stars_spent', 'stars_milestone']
    const heroCountUp = ref(0)
    let countUpFrame = null

    function startHeroCountUp(target) {
        cancelAnimationFrame(countUpFrame)
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            heroCountUp.value = target
            return
        }
        heroCountUp.value = 0
        const startedAt = performance.now()
        const duration = 700
        const step = (now) => {
            const progress = Math.min((now - startedAt) / duration, 1)
            heroCountUp.value = Math.round(target * (1 - Math.pow(1 - progress, 3)))
            if (progress < 1) countUpFrame = requestAnimationFrame(step)
        }
        countUpFrame = requestAnimationFrame(step)
    }

    watch(() => successModal.open, (open) => {
        if (!open) return
        const heroBadge = successModal.stats?.badges?.[0]
        if (heroBadge && COUNT_UP_TYPES.includes(heroBadge.type)) {
            startHeroCountUp(heroBadge.value)
        }
    })

    function badgeLabel(badge, value) {
        switch (badge.type) {
            case 'first_purchase': return 'First purchase — welcome to the Star Shop!'
            case 'top_purchaser': return "You're our #1 purchaser"
            case 'top_fan': return badge.rank === 1
                ? `You're the #1 fan of ${badge.performer_name}`
                : `Top ${badge.rank} fan of ${badge.performer_name}`
            case 'top_percentile': return `Top ${value}% of all purchasers`
            case 'streak': return `${value}-day purchase streak`
            case 'stars_milestone': return `${value.toLocaleString()}+ stars spent all-time`
            case 'order_milestone': return `That's purchase #${value}!`
            case 'stars_spent': return `${value.toLocaleString()} stars spent all-time`
            default: return null
        }
    }

    const statBadges = computed(() => {
        const badges = successModal.stats?.badges ?? []
        return badges
            .map((badge, i) => {
                const hero = i === 0
                const value = hero && COUNT_UP_TYPES.includes(badge.type) ? heroCountUp.value : badge.value
                const label = badgeLabel(badge, value)
                return label ? { type: badge.type, label, icon: BADGE_ICONS[badge.type] ?? 'sparkle', hero } : null
            })
            .filter(Boolean)
    })

    function isUrl(value) {
        try { return ['http:', 'https:'].includes(new URL(value).protocol) }
        catch { return false }
    }

    // Copy inside the success modal varies by what was purchased: direct
    // access / vault unlocks reveal an access link, everything else with a
    // claimable value is a discount link/code.
    const successCopy = computed(() => {
        const value = successModal.discountItemValue
        if (successModal.mechanic === 'direct_access' || successModal.mechanic === 'vault_video') {
            return { title: 'Your unlock is ready.', noun: 'access link', eyebrow: 'Your access link', accent: '#FDBA74', theme: 'amber' }
        }
        return isUrl(value)
            ? { title: 'Your link is ready.', noun: 'discount link', eyebrow: 'Your discount link', accent: '#67E8F9', theme: 'cyan' }
            : { title: 'Your code is ready.', noun: 'discount code', eyebrow: 'Your discount code', accent: '#A3E635', theme: 'lime' }
    })

    async function contribute(productId, amount, performerId = null) {
        if (!isUserLoggedIn.value) {
            joinRedirect('star_shop_purchase')
            return
        }
        if (purchasing.value) return
        purchasing.value = true
        purchaseFlash.message = ''

        const purchaseProps = {
            purchase_mode: 'single',
            items_count: 1,
            total_stars: amount,
            product_stars: amount,
            mechanic: mechanicByProductId.value[productId] ?? 'unknown',
        }
        track('Star Shop Purchase Attempted', purchaseProps, productId)

        try {
            const res = await fetch(route('members2.shop.purchase'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content ?? '',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ product_id: productId, stars_to_contribute: amount, performer_id: performerId, source: 'star_shop', interstitial_placement: 'star_shop_success' }),
            })
            const data = await res.json()
            if (data.success) {
                track('Star Shop Purchase Completed', {
                    ...purchaseProps,
                    stars_charged: amount,
                    stars_committed: amount,
                }, productId)
                successModal.message = data.message
                successModal.discountItemValue = data.discount_item_value ?? null
                successModal.mechanic = mechanicByProductId.value[productId] ?? null
                successModal.stats = data.stats ?? null
                successModal.interstitialAd = data.interstitial_ad ?? null
                successModal.interstitialAdSkipReason = data.interstitial_ad_skip_reason ?? null
                successModal.productId = productId
                successModal.starCost = amount
                successModal.performerId = performerId
                successModal.open = true
                if (!data.discount_item_value) {
                    // Nothing to reveal (shouldn't happen for vault/direct access,
                    // but keep parity with Index.vue's contribute()).
                }
            } else if (data.code === 'membership_required') {
                track('Star Shop Purchase Failed', {
                    ...purchaseProps,
                    error_reason: 'membership_required',
                    error_message: data.message,
                }, productId)
                openMembershipUpsell(data.membership_status, productId, amount, performerId)
            } else if (data.message === 'Insufficient star balance.') {
                track('Star Shop Purchase Failed', {
                    ...purchaseProps,
                    error_reason: 'insufficient_balance',
                    error_message: data.message,
                }, productId)
                openBuyStarsModal('star_shop_product', productId)
            } else {
                track('Star Shop Purchase Failed', {
                    ...purchaseProps,
                    error_reason: 'other',
                    error_message: data.message,
                }, productId)
                purchaseFlash.type = 'error'
                purchaseFlash.message = data.message
                clearTimeout(flashTimer)
                flashTimer = setTimeout(() => { purchaseFlash.message = '' }, 5000)
            }
        } catch {
            track('Star Shop Purchase Failed', {
                ...purchaseProps,
                error_reason: 'network',
                error_message: 'Request failed',
            }, productId)
            purchaseFlash.type = 'error'
            purchaseFlash.message = 'Something went wrong. Please try again.'
            clearTimeout(flashTimer)
            flashTimer = setTimeout(() => { purchaseFlash.message = '' }, 5000)
        } finally {
            purchasing.value = false
        }
    }

    // Interstitial Ad shown right after the success modal closes (trigger:
    // 'post_purchase') — a separate reactive object so InterstitialAdModal
    // owns its own open/close lifecycle instead of piggybacking on
    // successModal's.
    const interstitialAd = reactive({ open: false, creative: null, productId: null, starCost: null, performerId: null })

    /**
     * Enriches the bare purchased_product_id with the same product_type/
     * product_name shape every other Star Shop event already carries (see
     * track() above) — so Served/Clicked/Dismissed events are self-sufficient
     * for reporting without a join back to the product table. Also carries
     * the same membership_* fields track() already sends for every other
     * event, from the same analyticsContext dep, plus the performer whose
     * product triggered this ad (performerId is threaded through from
     * contribute()'s own param — same id already sent to the purchase
     * endpoint — via successModal, not re-derived here).
     */
    const interstitialAdContext = computed(() => ({
        purchased_product_id: interstitialAd.productId,
        purchased_product_type: interstitialAd.productId ? (mechanicByProductId.value[interstitialAd.productId] ?? null) : null,
        purchased_product_name: interstitialAd.productId ? (productNameById.value[interstitialAd.productId] ?? null) : null,
        purchased_star_cost: interstitialAd.starCost,
        performer_id: interstitialAd.performerId,
        performer_name: interstitialAd.performerId ? (performerNameById.value[interstitialAd.productId] ?? null) : null,
        membership_lifecycle_stage: analyticsContext.value?.membership_lifecycle_stage ?? null,
        membership_term: analyticsContext.value?.membership_term ?? null,
        membership_tier: analyticsContext.value?.membership_tier ?? null,
        is_gold: analyticsContext.value?.is_gold ?? null,
    }))

    function closeSuccessModal() {
        successModal.open = false
        const ad = successModal.interstitialAd
        const skipReason = successModal.interstitialAdSkipReason
        const productId = successModal.productId
        const starCost = successModal.starCost
        const performerId = successModal.performerId
        successModal.discountItemValue = null
        successModal.mechanic = null
        successModal.stats = null
        successModal.interstitialAd = null
        successModal.interstitialAdSkipReason = null
        successModal.productId = null
        successModal.starCost = null
        successModal.performerId = null

        if (ad) {
            interstitialAd.creative = ad
            interstitialAd.productId = productId
            interstitialAd.starCost = starCost
            interstitialAd.performerId = performerId
            interstitialAd.open = true
        } else {
            // Same eventProps() shape InterstitialAdModal.vue uses for
            // Served/Clicked/Dismissed (source/placement/trigger/creative_id/
            // product_line/user_id + context), so this event slots into the
            // same Tinybird pipe/report — skip_reason is the one new field.
            if (skipReason) {
                trackEvent('Interstitial Ad Suppressed', {
                    source: interstitialAdSource,
                    placement: 'star_shop_success',
                    trigger: 'post_purchase',
                    creative_id: null,
                    product_line: null,
                    user_id: page.props.auth?.user?.id ?? null,
                    skip_reason: skipReason,
                    purchased_product_id: productId,
                    purchased_product_type: productId ? (mechanicByProductId.value[productId] ?? null) : null,
                    purchased_product_name: productId ? (productNameById.value[productId] ?? null) : null,
                    purchased_star_cost: starCost,
                    performer_id: performerId,
                    performer_name: performerId ? (performerNameById.value[productId] ?? null) : null,
                    membership_lifecycle_stage: analyticsContext.value?.membership_lifecycle_stage ?? null,
                    membership_term: analyticsContext.value?.membership_term ?? null,
                    membership_tier: analyticsContext.value?.membership_tier ?? null,
                    is_gold: analyticsContext.value?.is_gold ?? null,
                })
            }
            router.reload()
        }
    }

    function closeInterstitialAd() {
        interstitialAd.open = false
        interstitialAd.creative = null
        interstitialAd.productId = null
        interstitialAd.starCost = null
        interstitialAd.performerId = null
        router.reload()
    }

    function copyDiscountValue() {
        if (!successModal.discountItemValue) return
        const text = successModal.discountItemValue
        const fallback = () => {
            const el = document.createElement('textarea')
            el.value = text
            el.style.cssText = 'position:fixed;opacity:0'
            document.body.appendChild(el)
            el.select()
            document.execCommand('copy')
            document.body.removeChild(el)
        }
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).catch(fallback)
        } else {
            fallback()
        }
        purchaseFlash.type = 'success'
        purchaseFlash.message = isUrl(successModal.discountItemValue) ? 'Link copied to clipboard!' : 'Code copied to clipboard!'
        clearTimeout(flashTimer)
        flashTimer = setTimeout(() => { purchaseFlash.message = '' }, 3000)
    }

    // ─── Buy Stars modal ────────────────────────────────────────────────────
    const buyStarsModalOpen = ref(false)
    const tosAccepted = ref(false)
    const tosShake = ref(false)
    const pendingProductContext = ref(null)
    const buyStarsModalSource = ref('unknown')
    const buyStarsModalProductId = ref(null)
    const buyStarsModalOpenedAt = ref(null)
    const buyStarsModalPackSelected = ref(false)

    function openBuyStarsModal(source = 'unknown', productId = null) {
        buyStarsModalOpen.value = true
        pendingProductContext.value = productId
            ? `${mechanicByProductId.value[productId] ?? 'unknown'}:${productId}`
            : null
        buyStarsModalSource.value = source
        buyStarsModalProductId.value = productId
        buyStarsModalOpenedAt.value = Date.now()
        buyStarsModalPackSelected.value = false
        track('Star Shop Buy Stars Modal Opened', {
            origin: source,
            origin_trigger: source === 'star_shop_product' ? 'insufficient_balance' : 'direct',
        }, productId)
        trackStarShopBounceEvent('modal_opened', { source, location, product_id: productId })
    }

    function closeBuyStarsModal(method) {
        track('Star Shop Buy Stars Modal Closed', {
            origin: buyStarsModalSource.value,
            origin_trigger: buyStarsModalSource.value === 'star_shop_product' ? 'insufficient_balance' : 'direct',
            close_method: typeof method === 'string' ? method : 'x',
            open_duration_ms: buyStarsModalOpenedAt.value ? Date.now() - buyStarsModalOpenedAt.value : null,
            pack_selected: buyStarsModalPackSelected.value,
        }, buyStarsModalProductId.value)
        trackStarShopBounceEvent('modal_closed', { source: buyStarsModalSource.value, location, product_id: buyStarsModalProductId.value })
        buyStarsModalOpen.value = false
    }

    function handleModalPackSelected(pack) {
        buyStarsModalPackSelected.value = true
        track('Star Shop Star Pack Selected', {
            stars: pack.stars,
            price: pack.price,
            label: pack.label,
            origin: buyStarsModalSource.value,
        }, buyStarsModalProductId.value)
    }

    function handleBuyStarPack(pack) {
        const returnUrl = window.location.pathname + window.location.search
        const source = pendingProductContext.value
            ? `StarShop:${pendingProductContext.value}`
            : 'StarShop'
        openStarPackPurchaseUrl(pack, source, returnUrl)
    }

    function handleModalBuyStarPack(pack) {
        track('Star Shop Star Pack Purchase Attempted', {
            stars: pack.stars,
            price: pack.price,
            label: pack.label,
            trigger: 'buy_stars_modal',
            origin: buyStarsModalSource.value,
        }, buyStarsModalProductId.value)
        trackStarShopBounceEvent('buy_clicked', { source: buyStarsModalSource.value, location, product_id: buyStarsModalProductId.value })
        handleBuyStarPack(pack)
    }

    function handlePackBuyClick(pack) {
        if (!tosAccepted.value) {
            tosShake.value = true
            setTimeout(() => { tosShake.value = false }, 600)
            return
        }
        track('Star Shop Star Pack Purchase Attempted', {
            stars: pack.stars,
            price: pack.price,
            label: pack.label,
            trigger: 'pack_rail',
            origin: 'direct',
        })
        handleBuyStarPack(pack)
    }

    watch(tosAccepted, (val) => {
        if (val) track('Star Shop TOS Accepted')
    })

    return {
        balance,
        isUserLoggedIn,
        track,
        joinRedirect,
        copied,
        copyShareLink,

        purchasing,
        purchaseFlash,
        contribute,

        membershipUpsell,
        openMembershipUpsell,
        closeMembershipUpsell,
        trackMembershipUpsellCta,

        successModal,
        successCopy,
        statBadges,
        closeSuccessModal,
        copyDiscountValue,
        isUrl,

        interstitialAd,
        interstitialAdContext,
        closeInterstitialAd,

        buyStarsModalOpen,
        buyStarsModalSource,
        buyStarsModalProductId,
        tosAccepted,
        tosShake,
        openBuyStarsModal,
        closeBuyStarsModal,
        handleModalPackSelected,
        handleModalBuyStarPack,
        handlePackBuyClick,
    }
}
